import I18n from "i18n-js";
import React from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { arrayOf, func, number, shape } from "prop-types";
import { connect } from "react-redux";
import { getInstallationsCount, getInstallationsFilters, getInstallationsList } from "../actions/installationsActions";
import pathToRegexp from "path-to-regexp";
import List from "../../common/components/layout/List";
import Quantity from "../../common/components/elements/Quantity";
import Select from "../../common/components/form/Select";
import { alphabetically } from "../../common/utils/filterUtils";
import StatelessCheckbox from "../../common/components/form/StatelessCheckbox";
import ClientUtils from "../../company/utils/ClientUtils";
import {
  ROUTE_INSTALLATIONS_IMPORT,
  ROUTE_INSTALLATION,
  ROUTE_INSTALLATION_EDIT,
} from "../../../routes";
import ClientTypes from "../../company/models/ClientTypes";
import Roles from "../../users/models/Roles";
import { formatDate } from "../../common/utils/dateUtils";
import ArchiveRequestAction from "../actions/ArchiveRequestAction";
import StatelessTable from "../../common/components/elements/StatelessTable";

class InstallationsList extends List {
  static propTypes = {
    getInstallationsList: func.isRequired,
    installations: shape().isRequired,
    installationsList: arrayOf(shape()).isRequired,
    getFilters: func.isRequired,
    filtersList: shape(),
    getCount: func.isRequired,
    count: number.isRequired,
    location: shape().isRequired,
    userProfile: shape().isRequired,
  };

  constructor(props) {
    super(props, {
      archivedInstallations: [],
    });

    this.refreshDataState = this.refreshDataState.bind(this);
    this.dataTableRow = this.dataTableRow.bind(this);
    this.archiveInstallation = this.archiveInstallation.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.onUpdateUrl = this.onUpdateUrl.bind(this);
    this.installationRoute = pathToRegexp.compile(ROUTE_INSTALLATION);
    this.installationEditRoute = pathToRegexp.compile(ROUTE_INSTALLATION_EDIT);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    super.componentDidMount(this.fetchData);
  }

  fetchData() {
    const { getInstallationsList, getFilters, getCount } = this.props;

    const { filters } = this.state;

    getInstallationsList(filters);
    getFilters(filters);
    getCount(filters);
  }

  /**
   * @param {Object} prevState
   */
  refreshDataState(prevState) {
    const { client } = this.state.filters;

    if (!client || prevState.oldFilters.client !== client) {
      const newFilters = {
        ...this.state.filters,
        site: null,
      };
      this.updateUrl(newFilters, this.state.filters);
      this.setState({
        filters: newFilters,
      });
    }
  }

  archiveInstallation(installationUuid) {
    this.setState({
      archivedInstallations: [
        ...this.state.archivedInstallations,
        installationUuid,
      ],
    });
  }

  /**
   * @return {Array}
   */
  dataTableHead() {
    return [
      I18n.t("pages.installations.head.externalId"),
      I18n.t("pages.installations.head.client"),
      I18n.t("pages.installations.head.site"),
      I18n.t("pages.installations.head.name"),
      I18n.t("pages.installations.head.mark"),
      I18n.t("pages.installations.head.fluid"),
      I18n.t("pages.installations.head.nominal_load"),
      I18n.t("pages.installations.head.current_load"),
      I18n.t("pages.installations.head.to_repair"),
      I18n.t("pages.installations.head.must_be_checked_after_repair"),
      I18n.t("pages.installations.head.expecting_leak_detection"),
      I18n.t("pages.installations.head.expecting_oil_analysis"),
      "",
    ];
  }

  /**
   * @param {Object} rowData
   *
   * @return {Array}
   */
  dataTableRow(rowData) {
    const notSpecified = <small>{I18n.t("common.not_specified")}</small>;
    const inversedBooleanIndic = (fail, warn = null) => {
      const doWarn = true === warn;
      return (
        <i
          className={classNames({
            fa: true,
            "text-success": !fail && !doWarn,
            "text-danger": fail,
            "text-warning": !fail && doWarn,
            "fa-check": !fail && !doWarn,
            "fa-times": fail,
            "fa-warning": !fail && doWarn,
          })}
          aria-hidden="true"
        />
      );
    };

    const toRepairIndic = inversedBooleanIndic(rowData.hasLeaks);
    const toBeCheckedAfterRepairIndic = inversedBooleanIndic(
      rowData.mustBeCheckedAfterRepair
    );
    const expectLeakDetectionIndic = (
      <span className={"text-nowrap"}>
        {inversedBooleanIndic(
          rowData.expectLeakDetection,
          rowData.expectLeakDetectionSoon
        )}
        &nbsp;
        {rowData.nextLeakDetectionDate &&
          formatDate(rowData.nextLeakDetectionDate)}
      </span>
    );
    const expectOilAnalysisIndic = (
      <span className={"text-nowrap"}>
        {inversedBooleanIndic(rowData.expectOilAnalysisSoon)}
        &nbsp;
        {rowData.nextOilAnalysisDate &&
          formatDate(rowData.nextOilAnalysisDate)}
      </span>
    );

    const archiveAction = !rowData.isDeleted &&
      !this.state.archivedInstallations.includes(rowData.uuid) && (
        <ArchiveRequestAction
          uuid={rowData.uuid}
          onDelete={this.archiveInstallation}
          key="archive"
        />
      );

    const viewAction = (
      <Link
        to={this.installationRoute({ uuid: rowData.uuid })}
        className="action"
      >
        <i className="fa-solid fa-eye" aria-hidden="true" />
      </Link>
    );

    const editAction = (
      <Link
        to={this.installationEditRoute({ uuid: rowData.uuid })}
        className="action"
      >
        <i className="fa-solid fa-pencil" aria-hidden="true" />
      </Link>
    );

    const actions = (
      <div>
        {archiveAction}
        {viewAction}
        {editAction}
      </div>
    );

    return [
      rowData.externalId || notSpecified,
      ClientUtils.renderLabel(rowData.client),
      rowData.site.designation,
      rowData.designation || notSpecified,
      rowData.mark || notSpecified,
      rowData.fluid.designation,
      <Quantity key="nominalLoad" value={rowData.nominalLoad} />,
      <Quantity key="currentLoad" value={rowData.currentLoad} />,
      toRepairIndic,
      toBeCheckedAfterRepairIndic,
      expectLeakDetectionIndic,
      expectOilAnalysisIndic,
      actions,
    ];
  }

  userCanImportInstallations() {
    const { roles, clientType } = this.props.userProfile;
    // At least MANAGER role & not a company user
    return roles.includes(Roles.MANAGER) && clientType !== ClientTypes.COMPANY;
  }

  getFilteredList() {
    const showDeleted = ["true", true].includes(this.state.filters.isDeleted);


    if (showDeleted) {
      return this.props.installationsList;
    }

    return this.props.installationsList.filter(
      (installation) => !this.state.archivedInstallations.includes(installation.uuid),
    );
  }

  /**
   * {@inheritdoc}
   */
  renderFilters() {
    const { filtersList } = this.props;
    const { filters } = this.state;

    const clients = filtersList?.clients || [];
    const sites = filtersList?.sites || [];
    const fluids = filtersList?.fluids || [];

    return [
      <Select
        key="filterByClient"
        id="filterByClient"
        onChange={(event) =>
          this.onChangeFilter(event, "client", this.refreshDataState)
        }
        label={I18n.t("filters.by_client")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {ClientUtils.renderLabel(option)}
          </option>
        )}
        options={clients}
        defaultValue={filters.client}
        placeholder={I18n.t("filters.by_client:default")}
        optional
      />,
      <Select
        key="filterBySite"
        id="filterBySite"
        onChange={(event) => this.onChangeFilter(event, "site")}
        label={I18n.t("filters.by_site")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={sites}
        defaultValue={filters.site}
        placeholder={I18n.t("filters.by_site:default")}
        optional
      />,
      <Select
        key="filterByFluid"
        id="filterByFluid"
        onChange={(event) => this.onChangeFilter(event, "fluid", null, true)}
        label={I18n.t("filters.by_fluid")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={fluids}
        defaultValue={filters.fluid}
        placeholder={I18n.t("filters.by_fluid:default")}
        optional
      />,
      <StatelessCheckbox
        key="filterWithLeaks"
        id="filterWithLeaks"
        label={I18n.t("filters.with_leaks")}
        checked={Boolean(filters.hasLeaks)}
        onChangedValue={(checked) => this.onChangeCheckboxFilter(checked, "hasLeaks")}
      />,
      <StatelessCheckbox
        key="filterToCheckAfterRepair"
        id="filterToCheckAfterRepair"
        label={I18n.t("filters.must_be_checked_after_repair")}
        checked={Boolean(filters.mustBeCheckedAfterRepair)}
        onChangedValue={(checked) =>
          this.onChangeCheckboxFilter(checked, "mustBeCheckedAfterRepair")
        }
      />,
      <StatelessCheckbox
        key="filterSoon"
        id="filterExpectLeakDetectionSoon"
        label={I18n.t("filters.expecting_leak_detection")}
        checked={Boolean(filters.expectLeakDetectionSoon)}
        onChangedValue={(checked) =>
          this.onChangeCheckboxFilter(checked, "expectLeakDetectionSoon")
        }
      />,
      <StatelessCheckbox
        key="filterAnalysisSoon"
        id="filterExpectOilAnalysisSoon"
        label={I18n.t("filters.expecting_oil_analysis")}
        checked={Boolean(filters.expectOilAnalysisSoon)}
        onChangedValue={(checked) =>
          this.onChangeCheckboxFilter(checked, "expectOilAnalysisSoon")
        }
      />,
      <StatelessCheckbox
        key="filterIsDeleted"
        id="filterIsDeleted"
        label={I18n.t("filters.is_deleted")}
        checked={Boolean(filters.isDeleted)}
        onChangedValue={(checked) =>
          this.onChangeCheckboxFilter(checked, "isDeleted")
        }
      />,
      <StatelessCheckbox
        key="filterDisassembly"
        id="filterDisassembly"
        label={I18n.t("filters.is_disassembled")}
        checked={Boolean(filters.isDisassembly)}
        onChangedValue={(checked) =>
          this.onChangeCheckboxFilter(checked, "isDisassembly")
        }
      />,
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { installations } = this.props;

    if (installations.error) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    const filteredInstallations = this.getFilteredList();

    return (
      <StatelessTable
        emptyContent={I18n.t("pages.installations.list.empty")}
        data={filteredInstallations}
        manualPagination
        dataCount={this.props.count}
        // columnStyle={[null, null, null, "number", "number", null, null, null]}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        filters={this.renderFilters()}
        pagination
        onPageChanged={this.onPageChanged}
        currentPage={this.getCurrentPage()}
        inProgress={installations.inProgress}
        filtersInProgress={installations.filtersInProgress}
        actions={
          <div>
            {this.userCanImportInstallations() && (
              <Link to={ROUTE_INSTALLATIONS_IMPORT} className="btn btn-primary">
                <i className="fa-solid fa-file" />{" "}
                {I18n.t("pages.installations.list.actions.import")}
              </Link>
            )}
          </div>
        }
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  renderTitle() {
    return I18n.t("pages.installations.list.title");
  }

  onUpdateUrl(newFilters) {
    const { getInstallationsList, getCount } = this.props;

    getInstallationsList(newFilters);
    getCount(newFilters);
  }
}

const mapStateToProps = (state) => ({
  installations: state.installations,
  installationsList: state.installations.list.sort(
    (a, b) =>
      alphabetically(a.client.legalCompanyName, b.client.legalCompanyName) ||
      alphabetically(a.site.designation, b.site.designation) ||
      alphabetically(a.designation, b.designation)
  ),
  filtersList: state.installations.filters,
  count: state.installations.count,
  userProfile: state.auth.userProfile,
});

const mapDispatchToProps = (dispatch) => ({
  getInstallationsList: (filters = {}) => dispatch(getInstallationsList(filters)),
  getFilters: (filters = {}) => dispatch(getInstallationsFilters(filters)),
  getCount: (filters = {}) => dispatch(getInstallationsCount(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InstallationsList);
